import { QueryKey } from '@tanstack/react-query'
import { isModelVirtualWeatherStation } from '@weenat/client/dist/core/devices'
import {
  ClusterProperties,
  PointProperties,
  computeClusterDisplayValue
} from '@weenat/client/dist/core/measurements'
import { getCountByMetric } from '@weenat/client/dist/core/metrics'
import { Universe } from '@weenat/client/dist/core/universes'
import useDisplayParams from '@weenat/client/dist/hooks/useDisplayParams'
import {
  Summary,
  SummaryData,
  isDeviceSummary,
  isPlotSummary,
  isStationSummary
} from '@weenat/client/dist/resources/measurements.type'
import { Href } from '@weenat/client/dist/routx/runtime-core'
import { useOrgContext } from 'app/orgProvider'
import { useClusterSettings, useUniverse, useUniverseHorizon } from 'app/state'
import { debounce } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDeepCompareEffect } from 'react-use'
import Supercluster from 'supercluster'
import useSupercluster from 'use-supercluster'
import {
  useBackgroundMapContext,
  useBackgroundMapDispatcher
} from '../contexts/BackgroundMapContext'
import DataCluster from '../markers/DataCluster'
import DataMarker from '../markers/DataMarker'
import useUniverseBackgroundMapContext from '../universes/useUniverseBackgroundMapContext'

/**
 * Redesigned clustering config — APP-4410
 * Viewport-level gate: clustering only activates when more than CLUSTERING_THRESHOLD
 * markers are visible. Below this count every marker renders individually, avoiding
 * the extra interaction cost of exploding clusters on sparse maps.
 */
const CLUSTERING_THRESHOLD_HIGH = 50
const CLUSTERING_THRESHOLD_LOW = 40

/**
 * Zoom gate: Supercluster generates clusters up to and including maxZoom.
 * At zoom 13+ individual markers are always shown regardless of density.
 */
const CLUSTERING_MAX_ZOOM = 12

export const TOOLTIP_CLOSE_DELAY_MS = 400

interface UsePODClusterArgs {
  isPlotsMap?: boolean
  isFetching: boolean
  summaries: Summary[]
  summariesQueryCacheKey?: QueryKey
}

/**
 * POD stands for plots or devices
 */
const usePODClusters = ({ summaries, isFetching, summariesQueryCacheKey }: UsePODClusterArgs) => {
  const [universe] = useUniverse()
  const [clusterSettings] = useClusterSettings()

  const org = useOrgContext()
  const { focusedMetricId, aggregate, universeAggregate } = useUniverseBackgroundMapContext()
  const [horizon] = useUniverseHorizon()

  const shouldClusterRef = useRef(false)

  const {
    mapDetails,
    api: { map }
  } = useBackgroundMapContext<Supercluster.PointFeature<ClusterProperties | PointProperties>>()

  const { hideEmptyDataPOD } = useDisplayParams()
  const dispatch = useBackgroundMapDispatcher()

  const markers: Supercluster.PointFeature<PointProperties & { resourceId: number }>[] = useMemo(
    () =>
      summaries.reduce(
        (acc, summary) => {
          // A plot or a device
          const resource = isPlotSummary(summary)
            ? summary.metadata.plot
            : isDeviceSummary(summary)
              ? summary.metadata.device
              : summary.metadata.station

          const { id: resourceId, location } = resource

          // Because there is no point of adding a point that has no location to the map
          if (!isNil(location)) {
            const { countByMetric, isVirtualDeviceByMetric } = getCountByMetric(summary)

            acc.push({
              type: 'Feature',
              id: resourceId,
              properties: {
                resourceId,
                countByMetric,
                isVirtualDeviceByMetric,
                summary: summary,
                isFavorite: !isNil(resource) && 'isFavorite' in resource && resource.isFavorite,
                kind: isPlotSummary(summary)
                  ? 'plot'
                  : isDeviceSummary(summary)
                    ? 'device'
                    : 'station'
              },
              geometry: {
                type: 'Point',
                coordinates: location.coordinates
              }
            })
          }

          return acc
        },
        [] as Supercluster.PointFeature<PointProperties & { resourceId: number }>[]
      ),
    [summaries]
  )

  // Counts how many markers currently fall inside the visible viewport bounds.
  // Used to decide whether clustering should be active at all (see shouldCluster).
  const viewportCount = useMemo(() => {
    if (isNil(mapDetails.bbox)) return markers.length
    const [west, south, east, north] = mapDetails.bbox
    return markers.filter(
      ({
        geometry: {
          coordinates: [lng, lat]
        }
      }) => lng >= west && lng <= east && lat >= south && lat <= north
    ).length
  }, [markers, mapDetails.bbox])

  // With the redesign, clustering is only applied when enough markers are visible and
  // the user has not disabled it via the display settings toggle.

  const shouldCluster = useMemo(() => {
    const threshold = shouldClusterRef.current
      ? CLUSTERING_THRESHOLD_LOW
      : CLUSTERING_THRESHOLD_HIGH
    const newShouldCluster = clusterSettings.enabled && viewportCount > threshold
    shouldClusterRef.current = newShouldCluster
    return newShouldCluster
  }, [viewportCount, clusterSettings])

  const superClusterOptions = useMemo(() => {
    const options: Supercluster.Options<
      PointProperties & { resourceId: number },
      {
        ids: Set<number>
        countByMetric: PointProperties['countByMetric']
        summaries: SummaryData[]
        isVirtualDeviceByMetric: PointProperties['isVirtualDeviceByMetric']
      }
    > = {
      reduce: (cluster, point) => {
        const keySet = new Set([
          ...Array.from(cluster.countByMetric.keys()),
          ...Array.from(point.countByMetric.keys())
        ])
        const newCountByMetric = new Map()
        const newIsVirtualDeviceByMetric = new Map()
        for (const key of keySet) {
          const metricCount =
            (cluster.countByMetric.get(key) ?? 0) + (point.countByMetric.get(key) ?? 0)

          newCountByMetric.set(key, metricCount)
          const isVirtualMetric =
            (cluster.isVirtualDeviceByMetric.get(key) ?? false) &&
            (point.isVirtualDeviceByMetric.get(key) ?? false)
          newIsVirtualDeviceByMetric.set(key, isVirtualMetric)
        }
        const newSummaries: SummaryData[] = []
        newSummaries.push(...cluster.summaries, ...point.summaries)
        cluster.summaries = newSummaries
        cluster.countByMetric = newCountByMetric
        cluster.isVirtualDeviceByMetric = newIsVirtualDeviceByMetric
        cluster.ids = new Set([...Array.from(cluster.ids), ...Array.from(point.ids)])
      },
      map: (c) => ({
        countByMetric: new Map(c.countByMetric),
        summaries: [c.summary],
        isVirtualDeviceByMetric: new Map(c.isVirtualDeviceByMetric),
        ids: new Set<number>([c.resourceId])
      }),
      radius: clusterSettings.radius,
      maxZoom: CLUSTERING_MAX_ZOOM,
      // Infinity makes it impossible for Supercluster to form any cluster (a group of
      // Infinity nearby points can never be found), so every marker in the viewport is
      // returned as an individual point feature — effectively disabling clustering.
      minPoints: shouldCluster ? clusterSettings.minPoints : Infinity
    }

    return options
  }, [shouldCluster, clusterSettings])

  const { clusters, supercluster } = useSupercluster({
    // Don't feed points until bounds are known — without bounds, useSupercluster falls back to a
    // global bbox and returns all markers regardless of viewport (measured: ~3s at startup).
    points: !isNil(mapDetails.bbox) ? markers : [],
    zoom: !isNil(map) && !isNil(mapDetails.zoom) ? mapDetails.zoom : undefined,
    bounds: !isNil(map) && !isNil(mapDetails.bbox) ? mapDetails.bbox : undefined,
    options: superClusterOptions,
    disableRefresh: isFetching
  })

  const resetHoverIntent = useCallback(
    () =>
      dispatch({
        type: 'resetHoverIntent'
      }),
    [dispatch]
  )

  const debouncedReset = useMemo(
    () => debounce(resetHoverIntent, TOOLTIP_CLOSE_DELAY_MS),
    [resetHoverIntent]
  )

  const renderCluster = useCallback(
    (cluster: Supercluster.PointFeature<ClusterProperties | PointProperties>) => {
      const [longitude, latitude] = cluster.geometry.coordinates
      /**
       * Properties changes depending on whether the cluster actually is one or whether it's just a regular marker
       * for example, common cluster properties only exists on actual clusters (not markers)
       */
      const { isFavorite, isVirtualDeviceByMetric, countByMetric } = cluster.properties

      if ('cluster' in cluster.properties && cluster.properties.cluster && !isNil(supercluster)) {
        const { point_count: count, summaries: clusterSummaries } = cluster.properties

        if (count === 0) return null

        const explodeCluster = () => {
          if (isNil(map)) {
            return
          }

          // getClusterExpansionZoom returns the zoom at which this cluster splits
          // into its children. Using it (instead of fitBounds) guarantees the camera
          // always animates — fitBounds is a no-op when the cluster leaves are
          // already contained within the current viewport.
          const expansionZoom = supercluster.getClusterExpansionZoom(cluster.id as number)

          map.panTo({ lat: latitude, lng: longitude })
          map.setZoom(expansionZoom)
        }

        const { value, range, coloringValue, threshold, aggregateType, waterTankAlertCount } =
          computeClusterDisplayValue({
            summaries: clusterSummaries,
            focusedMetricId,
            aggregate,
            universeAggregate,
            horizon,
            zoom: mapDetails.zoom
          })

        const countOfFocusedMetric = !isNil(focusedMetricId)
          ? countByMetric.get(focusedMetricId)
          : null
        return (
          <DataCluster
            key={cluster.id}
            lat={latitude}
            lng={longitude}
            onPress={explodeCluster}
            count={
              hideEmptyDataPOD && !isNil(focusedMetricId) && !isNil(countOfFocusedMetric)
                ? countOfFocusedMetric
                : count
            }
            value={value}
            range={range}
            coloringValue={coloringValue}
            threshold={threshold}
            aggregateType={aggregateType}
            waterTankAlertCount={waterTankAlertCount}
            isVirtualDeviceByMetric={isVirtualDeviceByMetric}
          />
        )
      } else if ('summary' in cluster.properties) {
        const { summary, kind } = cluster.properties

        const summaryLink: Href =
          kind === 'plot'
            ? `/farms/${org.id}/plots/${cluster.id}/${
                universe === Universe.irrelis ? 'irrelis' : ''
              }`
            : kind === 'device'
              ? `/weather-map/${cluster.id}`
              : `/farms/${org.id}/stations/${cluster.id}`

        const model = isDeviceSummary(summary)
          ? summary.metadata.device.model
          : isStationSummary(summary)
            ? summary.metadata.station.model
            : null

        const type = !isNil(model)
          ? isModelVirtualWeatherStation(model)
            ? 'virtual_device'
            : 'device'
          : 'plot'

        const timezone = isDeviceSummary(summary)
          ? summary.metadata.device.timezone
          : isStationSummary(summary)
            ? summary.metadata.station.timezone
            : summary.metadata.plot.timezone

        return (
          <DataMarker
            lat={latitude}
            lng={longitude}
            isFavorite={isFavorite}
            key={cluster.id}
            countByMetric={countByMetric}
            summary={summary}
            isVirtualDeviceByMetric={isVirtualDeviceByMetric}
            summaryLink={summaryLink}
            resourceId={cluster.id}
            debouncedReset={debouncedReset}
            type={type}
            summariesQueryCacheKey={summariesQueryCacheKey}
            timezone={timezone}
          />
        )
      }
    },
    [
      supercluster,
      focusedMetricId,
      aggregate,
      universeAggregate,
      horizon,
      mapDetails.zoom,
      hideEmptyDataPOD,
      map,
      org.id,
      universe,
      debouncedReset,
      summariesQueryCacheKey
    ]
  )

  useDeepCompareEffect(() => {
    dispatch({ type: 'setMarkers', newMarkers: clusters })
  }, [clusters])

  useEffect(() => {
    dispatch({ type: 'setRenderMarkers', newRenderMarkers: renderCluster })
  }, [renderCluster, dispatch])

  // Return for debug with cluster debug tools
  // return { viewportCount }
}

export default usePODClusters
